import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  TimePicker,
  Spin,
} from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React, { useRef, useEffect, useState } from "react";
import { MODE_PREFERENCE } from "utils/constants";

export default function SearchFilter({
  setIsSearch,
  isSearch,
  golfCourseList,
  GetCaddies,
  formSearchRef,
  setFilterList,
  tfDateSearch,
  setTfDateSearch,
  minPrice,
  maxPrice,
  price,
  setPrice,
  setPage,
  isLoadingGolfCourses,
  setPathParams,
  setSearchFilter,
  searchFilter,
}) {
  const onChangeSearch = (formValues) => {
    const newParams = new URLSearchParams();

    setPage(1);
    setIsSearch(false);

    if (formValues.golfCourse) {
      newParams.set("golfCourseId", formValues.golfCourse);
    }

    if (formValues.caddyName) {
      newParams.set("search", formValues.caddyName);
    }

    if (formValues.teeOffDate) {
      const formattedDate = dayjs(formValues.teeOffDate).format("DD-MM-YYYY");
      const tfDate = dayjs(formValues.teeOffDate).format("YYYY-MM-DD");
      newParams.set("date", formattedDate);
      setTfDateSearch(tfDate);
    } else {
      setTfDateSearch(null);
    }

    if (formValues.teeOffTime) {
      newParams.set("time", dayjs(formValues.teeOffTime).format("HH:mm"));
    }

    if (formValues.priceRange) {
      const [min, max] = formValues.priceRange;
      newParams.set("minPrice", min);
      newParams.set("maxPrice", max);
      setPrice([min, max]);
    } else {
      setPrice([minPrice, maxPrice]);
    }

    if (formValues.modePreference) {
      newParams.set("modePreference", formValues.modePreference);
    }

    setSearchFilter({ ...Object.fromEntries(newParams) });
    setPathParams(newParams);
  };

  const disableDates = (current) => {
    const isBeforeToday = current && current < new Date().setHours(0, 0, 0, 0); // ปิดการใช้งานวันก่อนหน้า
    return isBeforeToday;
  };

  function handleSubmit(values) {
    onChangeSearch(values);
  }

  function clearSearch() {
    // Reset essential UI state
    setTfDateSearch(null);
    setPrice([minPrice, maxPrice]);
    setFilterList([]);

    // Reset form fields
    formSearchRef.current?.resetFields();
    formSearchRef.current?.setFieldsValue({
      golfCourse: undefined,
      caddyName: undefined,
      teeOffDate: undefined,
      teeOffTime: undefined,
      priceRange: [minPrice, maxPrice],
      modePreference: undefined,
    });
    setPage(1);

    setSearchFilter({});

    window.history.replaceState({}, "", window.location.pathname);
  }

  const useRefFormSearchRef = useRef(null);
  useEffect(() => {
    const handleTouchMove = (event) => {
      if (event.cancelable) {
        event.preventDefault();
      }
    };

    const observer = new MutationObserver(() => {
      const dropdown = document.querySelector(".touch-disable-dropdown");
      if (dropdown) {
        dropdown.addEventListener("touchmove", handleTouchMove, {
          passive: false,
        });
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      const dropdown = document.querySelector(".touch-disable-dropdown");
      if (dropdown) {
        dropdown.removeEventListener("touchmove", handleTouchMove);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div className="search-filter-container">
      <div className="search-filter-content">
        <Form
          layout="vertical"
          ref={formSearchRef}
          onFinish={handleSubmit}
          style={{ margin: 0 }}
        >
          <Form.Item
            name="golfCourse"
            rules={[{ required: false, message: "กรุณาเลือก `สนามกอล์ฟ`" }]}
            label={<b style={{ padding: 0 }}>สนามกอล์ฟ</b>}
            style={{ margin: 0 }}
          >
            <Select
              ref={useRefFormSearchRef}
              classNames={{
                popup: {
                  root: "touch-disable-dropdown",
                },
              }}
              popupProps={{
                style: {
                  position: "fixed",
                  zIndex: 10000,
                  maxHeight: 250,
                  overflowY: "auto",
                },
              }}
              allowClear
              loading={isLoadingGolfCourses}
              notFoundContent={
                isLoadingGolfCourses ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "8px 0",
                    }}
                  >
                    <Spin
                      indicator={
                        <LoadingOutlined
                          style={{
                            fontSize: 14,
                            color: "#1890ff",
                          }}
                          spin
                        />
                      }
                      size="small"
                    />
                    <span style={{ color: "#999", fontSize: 14 }}>
                      กำลังโหลดสนามกอล์ฟ...
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                      padding: "8px 0",
                    }}
                  >
                    <span style={{ color: "#999", fontSize: 14 }}>
                      ไม่พบสนามกอล์ฟ
                    </span>
                  </div>
                )
              }
              onChange={() => window.scrollTo(0, 0)}
              placeholder="เลือกสนามกอล์ฟ"
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase()?.includes(input.toLowerCase())
              }
              style={{
                width: "100%",
                height: 50,
                marginTop: -10,
              }}
            >
              {golfCourseList?.map((golfCourse) => (
                <Select.Option key={golfCourse.id} value={golfCourse.id}>
                  {golfCourse.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="caddyName"
            rules={[
              {
                required: false,
                message: "กรุณาเลือก `หมายเลขแคดดี้, ชื่อแคดดี้`",
              },
            ]}
            label={<b style={{ padding: 0 }}>หมายเลขแคดดี้, ชื่อแคดดี้</b>}
            style={{ margin: 0, marginBottom: 15 }}
          >
            <Input
              allowClear
              placeholder="ค้นหาแคดดี้"
              onSearch={() => GetCaddies(true)}
              style={{ height: 32 }}
            />
          </Form.Item>

          <div
            onClick={() => setIsSearch(!isSearch)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 5,
              padding: "8px 0",
              cursor: "pointer",
              marginBottom: 10,
              color: "#1890ff",
              fontSize: 14,
              fontWeight: 500,
              userSelect: "none",
            }}
          >
            <span>
              {isSearch ? "ซ่อนตัวกรองเพิ่มเติม" : "ตัวกรองเพิ่มเติม"}
            </span>
            {isSearch ? (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="18 15 12 9 6 15"></polyline>
              </svg>
            ) : (
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            )}
          </div>

          <div
            className={
              !isSearch ? "is-search-collapse" : "is-search-in-collapse"
            }
          >
            <Form.Item
              name="teeOffDate"
              rules={[
                {
                  required: false,
                  message: "กรุณากำหนด วันที่ทีออฟ (Tee-Off)`",
                },
              ]}
              label={<b>กำหนดวันที่ทีออฟ (Tee-Off)</b>}
            >
              <DatePicker
                allowClear
                placeholder="เลือกวันที่ทีออฟ"
                onChange={(date) => {
                  if (date) {
                    setTfDateSearch(dayjs(date).format("YYYY-MM-DD"));
                  } else {
                    setTfDateSearch(null);
                    formSearchRef.current?.setFieldValue("teeOffTime", null);
                  }
                }}
                disabledDate={disableDates}
                style={{ width: "100%", height: 50 }}
                format={"DD-MMMM-YYYY"}
                inputReadOnly={true}
              />
            </Form.Item>
            <Form.Item
              name="teeOffTime"
              rules={[
                {
                  required: false,
                  message: "กรุณากำหนด `เวลาทีออฟ (Tee-Off)`",
                },
              ]}
              label={<b> กำหนดเวลาทีออฟ (Tee-Off)</b>}
            >
              <TimePicker
                allowClear
                placeholder="เลือกเวลาที่ทีออฟ"
                disabled={!tfDateSearch}
                style={{ width: "100%", marginTop: 10, height: 50 }}
                format={"HH:mm"}
              />
            </Form.Item>
            <div style={{ margin: 0, marginBottom: 15 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  fontSize: 18,
                  width: "100%",
                  gap: 10,
                  marginBottom: 8,
                }}
              >
                <b>ราคา</b>
              </div>
              <div
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Form.Item
                  name={["priceRange", 0]}
                  style={{ margin: 0, flex: 1 }}
                >
                  <InputNumber
                    style={{ width: "100%", height: 30 }}
                    min={0}
                    onChange={(value) => {
                      const newPrice = [
                        value ?? minPrice,
                        price ? price[1] : maxPrice,
                      ];
                      setPrice(newPrice);
                    }}
                    placeholder={`${minPrice}`}
                  />
                </Form.Item>
                <b>จนถึง</b>
                <Form.Item
                  name={["priceRange", 1]}
                  style={{ margin: 0, flex: 1 }}
                >
                  <InputNumber
                    style={{ width: "100%", height: 30 }}
                    min={price ? price[0] : minPrice}
                    onChange={(value) => {
                      const newPrice = [
                        price ? price[0] : minPrice,
                        value ?? maxPrice,
                      ];
                      setPrice(newPrice);
                    }}
                    placeholder={`${maxPrice}`}
                  />
                </Form.Item>
                <b>บาท</b>
              </div>
            </div>
            <Form.Item
              name="modePreference"
              label={<b style={{ padding: 0 }}>ใช้รถหรือเดิน</b>}
              style={{ margin: 0, marginBottom: 15 }}
            >
              <Select
                allowClear
                placeholder="เลือกใช้รถหรือเดิน"
                style={{ width: "100%", height: 40 }}
              >
                {MODE_PREFERENCE.map((mode) => (
                  <Select.Option key={mode.value} value={mode.value}>
                    {mode.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </div>
          <Form.Item style={{ margin: 0 }}>
            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 10,
              }}
            >
              <Button
                onClick={() => {}}
                loading={isLoadingGolfCourses}
                htmlType="submit"
                type="primary"
                style={{
                  flex: 1,
                  height: "35px",
                  fontSize: 16,
                }}
              >
                ค้นหา
              </Button>
              <Button
                onClick={clearSearch}
                loading={isLoadingGolfCourses}
                type="default"
                style={{
                  flex: 1,
                  height: "35px",
                  fontSize: 16,
                  borderColor: "#d9d9d9",
                  color: "#666",
                }}
              >
                ล้างตัวกรอง
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
}
